// Ein Clonefile vom USB-Stick holen und hochladen (Samsung).
//
// ERZEUGEN kann die App keins: webapis.clone hat nur restoreCloneData und zwei
// Listener - so dokumentiert im Samsung-Partner-Training und so gemessen auf
// BU800 und U800F. Der Techniker exportiert es weiterhin am Muster-TV ueber das
// Hotelmenue auf einen Stick.
//
// LESEN kann sie ihn aber: tizen.filesystem meldet den Stick als
// "removable_sda1" und listet /opt/media/USBDriveA1 (gemessen auf einem
// U800F). Damit muss niemand mehr den Stick zum Rechner tragen - die Datei
// geht von hier direkt in den SocketAdmin, und von dort per Rollout auf die
// anderen Geraete.
//
// Auf LG geht das NICHT: hcap.file kann nur downloadFileToUsb (Netz -> USB),
// eine Gegenrichtung gibt es dort nicht.
//
// Hochgeladen wird per HTTP, nicht ueber den Socket: eine .sclone hat mehrere
// MB, und der Steuerkanal reicht JSON-Frames durch. Die Url samt kurzlebigem
// Token steht im Kommando.
//
// ACHTUNG, UNGEKLAERT: ein U800F hat sich beim ersten nativen Lesen einer Datei
// vom Stick (openStream + readBase64, 16 Bytes) neu gestartet. Das Auflisten
// desselben Sticks lief davor unauffaellig. Solange das nicht geklaert ist,
// laeuft dieses Modul nur auf ausdrueckliches Kommando - und das Kommando
// gehoert an ein Geraet, bei dem ein Neustart gerade nichts kostet, nicht an
// ein belegtes Zimmer.

import DeviceType from '@/helpers/DeviceType';

/** Ein einzelner Rueckwaertsschraegstrich, ohne Escape-Gefummel. */
const BACKSLASH = String.fromCharCode(92);

/** Zeitbudget je Dateisystem-Aufruf. */
const STEP_TIMEOUT_MS = 4000;

/** Wie viele Bytes je Leseschritt. 512 KB je Runde halten den Speicher flach. */
const CHUNK_BYTES = 512 * 1024;

/**
 * Zeitgrenze fuers Lesen und fuers Hochladen.
 *
 * Grosszuegig, weil eine .sclone mehrere MB hat und ein TV-WLAN langsam sein
 * darf - aber endlich. Ohne sie kann ein XHR, der weder onload noch onerror
 * meldet (eine nicht ladbare file://-Adresse tut das), ewig offen bleiben; im
 * Admin sah das aus wie "gar keine Rueckmeldung".
 */
const XHR_TIMEOUT_MS = 120000;

/** Obergrenze - dieselbe wie am Server, damit der Fehler hier auffaellt. */
const MAX_BYTES = 64 * 1024 * 1024;

/** Woran ein Clonefile zu erkennen ist. Samsung: CloneEncrypted.sclone. */
const CLONE_FILE_RE = /\.sclone$/i;
const CLONE_DIR_RE = /clone/i;

function fs() {
  try {
    return typeof tizen !== 'undefined' && tizen && tizen.filesystem ? tizen.filesystem : null;
  } catch (e) {
    return null;
  }
}

function withTimeout(promise, ms, fallback) {
  return Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(fallback), ms))]);
}

function resolveDir(label) {
  const api = fs();
  if (!api || typeof api.resolve !== 'function') return Promise.resolve(null);
  return withTimeout(
    new Promise((resolve) => {
      try {
        api.resolve(label, (dir) => resolve(dir), () => resolve(null), 'r');
      } catch (e) {
        resolve(null);
      }
    }),
    STEP_TIMEOUT_MS,
    null
  );
}

function listFiles(dir) {
  if (!dir || typeof dir.listFiles !== 'function') return Promise.resolve([]);
  return withTimeout(
    new Promise((resolve) => {
      try {
        dir.listFiles((files) => resolve(files || []), () => resolve([]));
      } catch (e) {
        resolve([]);
      }
    }),
    STEP_TIMEOUT_MS,
    []
  );
}

function nameOf(file) {
  try {
    if (file.name) return String(file.name);
  } catch (e) {
    /* bei den virtuellen Wurzeln wirft `name` */
  }
  try {
    return String(file.fullPath || '').split('/').pop() || '';
  } catch (e) {
    return '';
  }
}

function sizeOf(file) {
  try {
    return file.fileSize || 0;
  } catch (e) {
    return 0;
  }
}

function isDir(file) {
  try {
    return !!file.isDirectory;
  } catch (e) {
    return false;
  }
}

/**
 * Die Wechselmedien zuerst: dorthin schreibt das Hotelmenue. Ein Clonefile im
 * internen Speicher waere eins, das WIR heruntergeladen haben - das
 * zurueckzuschicken hat keinen Wert.
 */
function storageLabels() {
  const api = fs();
  if (!api || typeof api.listStorages !== 'function') return Promise.resolve([]);
  return withTimeout(
    new Promise((resolve) => {
      try {
        api.listStorages(
          (list) =>
            resolve(
              (list || [])
                .filter((s) => {
                  try {
                    return !s.state || String(s.state).toUpperCase().indexOf('MOUNT') !== -1;
                  } catch (e) {
                    return false;
                  }
                })
                .map((s) => {
                  try {
                    return s.label;
                  } catch (e) {
                    return '';
                  }
                })
                .filter((l) => l && /removable|usb|sdcard/i.test(l))
            ),
          () => resolve([])
        );
      } catch (e) {
        resolve([]);
      }
    }),
    STEP_TIMEOUT_MS,
    []
  );
}

/** Ordner, die auf einem Windows-Stick nur Zeit kosten. */
const SKIP_DIR_RE = /^(System Volume Information|\$RECYCLE\.BIN|LOST\.DIR|\.)/i;

/**
 * Der Ordner mit der EINEN transportablen Datei.
 *
 * So sieht ein Export aus (gemessen an einem U800F-Stick):
 *
 *   T-RSLFDEUC-0090/
 *     Clone/                      viele Dateien UND Ordner - art-app, avoc,
 *                                 CSFS, settings, vconf, was, ... kein
 *                                 einzelnes Artefakt, sondern ein Baum
 *     sClone/
 *       CloneEncrypted.sclone     das hier ist es
 *
 * `Clone` ist damit ausdruecklich NICHT die Quelle: die groesste Datei darin
 * waere `avoc/.avoc.db` - eine Datenbank, kein Clonefile.
 */
const CLONE_LEAF_RE = /^sclone$/i;

/**
 * Wie tief gesucht wird und wie viele Ordner dabei hoechstens aufgehen.
 *
 * Drei Ebenen, weil genau so tief die Datei liegt: der Stick traegt einen
 * Ordner mit dem Modellcode (gemessen: T-RSLFDEUC-0090), darin `Clone` und
 * `sClone`, und erst darin die Datei. Zwei Ebenen - der bisherige Stand -
 * kamen also nie an. Das Budget deckelt die Gesamtzeit: jedes Auflisten hat
 * vier Sekunden, und ein voller Stick hat viele Ordner.
 */
const MAX_DEPTH = 3;
const MAX_LISTINGS = 14;

/** Zaehlt die Auflistungen herunter - geteilt ueber alle Speicher hinweg. */
function makeBudget(count) {
  let left = count;
  return {
    take: function() {
      if (left <= 0) return false;
      left -= 1;
      return true;
    },
  };
}

/**
 * Eine .sclone auf einem Stick suchen - der Reihe nach, aber nicht blind.
 *
 * Verdaechtige Ordner zuerst: erst was nach Clone aussieht, dann der
 * Samsung-Modellordner (`T-...`), dann der Rest. Der Ordnername taugt naemlich
 * nicht als Filter - wie er heisst, legt die Firmware fest -, wohl aber als
 * Reihenfolge.
 *
 * `trace` sammelt, was tatsaechlich zu sehen war. Ohne diese Spur ist "nichts
 * gefunden" nicht zu unterscheiden von "an der falschen Stelle gesucht"; genau
 * daran hing dieser Fehler zwei Anlaeufe lang.
 */
function findCloneFile(label, trace, budget) {
  // Reihenfolge, keine Auswahl: `sClone` zuerst, denn dort liegt die Datei.
  // Danach der Modellordner, der beide enthaelt, dann alles Uebrige - und
  // `Clone` ganz zuletzt, weil sein Baum nur Budget kostet.
  const rank = (f) => {
    const n = nameOf(f);
    if (CLONE_LEAF_RE.test(n)) return 0;
    if (/^T-/i.test(n)) return 1;
    if (CLONE_DIR_RE.test(n)) return 3;
    return 2;
  };

  const walk = (dir, where, depth) => {
    if (!budget.take()) {
      trace.push(where + ': nicht mehr angesehen (Zeitbudget)');
      return Promise.resolve(null);
    }
    return listFiles(dir).then((entries) => {
      trace.push(
        where + ': ' + (entries.length ? entries.map(nameOf).join(', ') : 'leer')
      );

      const files = entries.filter((f) => !isDir(f));
      const hit = files.filter((f) => CLONE_FILE_RE.test(nameOf(f)));
      if (hit.length) return hit[0];

      // Rueckfall NUR in `sClone`: dort liegt genau eine Datei, und wenn die
      // Firmware ihr einmal eine andere Endung gibt, ist sie trotzdem die
      // richtige. In `Clone` gilt das ausdruecklich nicht - dort stehen
      // Dutzende Dateien, von denen keine das Clonefile ist.
      const leaf = where.split('/').pop() || '';
      if (CLONE_LEAF_RE.test(leaf) && files.length) {
        const biggest = files
          .map((f) => ({ f: f, size: sizeOf(f) }))
          .filter((e) => e.size > 0)
          .sort((a, b) => b.size - a.size)[0];
        if (biggest) {
          trace.push(where + ': ohne .sclone - nehme ' + nameOf(biggest.f));
          return biggest.f;
        }
      }

      if (depth >= MAX_DEPTH) return null;
      const dirs = entries
        .filter((f) => isDir(f) && !SKIP_DIR_RE.test(nameOf(f)))
        .sort((a, b) => rank(a) - rank(b));

      return dirs.reduce(
        (chain, sub) =>
          chain.then(
            (found) => found || walk(sub, where + '/' + nameOf(sub), depth + 1)
          ),
        Promise.resolve(null)
      );
    });
  };

  return resolveDir(label).then((dir) => {
    if (!dir) {
      trace.push(label + ': nicht lesbar');
      return null;
    }
    return walk(dir, label, 1);
  });
}

/**
 * Die Datei als Base64 lesen - in Haeppchen.
 *
 * readBase64 in einem Rutsch waere auf einem TV-WebView der sichere Weg in
 * einen Speicherfehler; die Haeppchen halten den Verbrauch flach. Der Stream
 * wird in jedem Fall geschlossen, auch wenn mittendrin etwas schiefgeht - ein
 * offener Datei-Handle auf einem Geraet, das niemand neu startet, bleibt sonst
 * bis zum naechsten Standby liegen.
 */
function readBase64(file) {
  return new Promise((resolve, reject) => {
    const size = sizeOf(file);
    if (size > MAX_BYTES) {
      reject(new Error('Datei zu gross (' + size + ' Bytes)'));
      return;
    }
    try {
      file.openStream(
        'r',
        function(stream) {
          const parts = [];
          try {
            while (!stream.eof) {
              const chunk = stream.readBase64(CHUNK_BYTES);
              if (!chunk) break;
              parts.push(chunk);
            }
          } catch (e) {
            try {
              stream.close();
            } catch (e2) {
              /* schon zu */
            }
            reject(e);
            return;
          }
          try {
            stream.close();
          } catch (e) {
            /* schon zu */
          }
          resolve(parts.join(''));
        },
        function(err) {
          reject(err);
        },
        'UTF-8'
      );
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Base64 aus einem ArrayBuffer - in Haeppchen, weil String.fromCharCode ein
 * Argument je Byte nimmt und ein ganzes Megabyte den Argument-Stapel sprengt.
 */
function bufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Die Datei ueber ihre Adresse lesen - der Weg OHNE natives Stream-Handling.
 *
 * tizen.filesystem.toURI() liefert eine file://-Adresse, und eine
 * privilegierte Tizen-App darf die laden. Das ist der bevorzugte Weg: das
 * native Lesen (openStream) hat einen U800F neu gestartet. Traegt er nicht,
 * bleibt der andere als Rueckfall - er steht darunter.
 */
function readViaUri(file) {
  const api = fs();
  let uri;
  try {
    if (api && typeof api.toURI === 'function') uri = api.toURI(file);
    else if (typeof file.toURI === 'function') uri = file.toURI();
  } catch (e) {
    return Promise.reject(new Error('toURI: ' + String((e && e.message) || e)));
  }
  if (!uri) return Promise.reject(new Error('toURI liefert keine Adresse'));

  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', uri, true);
      xhr.responseType = 'arraybuffer';
      xhr.onload = function() {
        const buf = xhr.response;
        if (!buf || !buf.byteLength) {
          reject(new Error('Datei ueber ' + uri + ' leer gelesen'));
          return;
        }
        if (buf.byteLength > MAX_BYTES) {
          reject(new Error('Datei zu gross (' + buf.byteLength + ' Bytes)'));
          return;
        }
        try {
          resolve(bufferToBase64(buf));
        } catch (e) {
          reject(e);
        }
      };
      xhr.onerror = function() {
        reject(new Error('Adresse nicht ladbar (' + uri + ')'));
      };
      xhr.timeout = XHR_TIMEOUT_MS;
      xhr.ontimeout = function() {
        reject(new Error('Lesen ueber die Adresse dauert zu lange (' + uri + ')'));
      };
      xhr.send();
    } catch (e) {
      reject(e);
    }
  });
}

function post(url, body) {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          let parsed;
          try {
            parsed = JSON.parse(xhr.responseText || '{}');
          } catch (e) {
            parsed = {};
          }
          resolve(parsed);
          return;
        }
        reject(new Error('HTTP ' + xhr.status + ' ' + String(xhr.responseText || '').slice(0, 200)));
      };
      xhr.onerror = function() {
        // Haeufigster Grund: der Server ist unter dieser Adresse vom Fernseher
        // aus nicht erreichbar, oder der Vorabflug (OPTIONS) wurde abgelehnt.
        reject(new Error('Netzwerkfehler beim Hochladen nach ' + url));
      };
      xhr.timeout = XHR_TIMEOUT_MS;
      xhr.ontimeout = function() {
        reject(new Error('Hochladen dauert zu lange (' + url + ')'));
      };
      xhr.send(JSON.stringify(body));
    } catch (e) {
      reject(e);
    }
  });
}

/** Rueckwaertsschraegstriche vereinheitlichen - der Vergleich unten ist ein
 *  reiner Textvergleich, und tizen.filesystem mischt beide Formen. */
function slashes(value) {
  return String(value).split(BACKSLASH).join('/');
}

/**
 * Eine Datei ueber ihren vollen Pfad aufloesen.
 *
 * Erst `resolve` - das ist der direkte Weg. Manche Firmware nimmt einen
 * absoluten Pfad aber nicht an (der Lesetest am U800F meldete auf toURI
 * "Invalid path."), deshalb danach der Weg zu Fuss: vom Speicher aus Segment
 * fuer Segment durch die Auflistungen, bis der Name passt. Langsamer, aber er
 * arbeitet mit denselben Objekten, die auch das Auflisten liefert.
 */
function resolveByPath(fullPath) {
  return resolveDir(fullPath).then((f) => {
    if (f && !isDir(f)) return f;
    return storageLabels().then((labels) => {
      const norm = slashes(fullPath);
      return labels.reduce(
        (chain, label) =>
          chain.then((found) => {
            if (found) return found;
            return resolveDir(label).then((root) => {
              if (!root) return null;
              const base = slashes(root.fullPath || '');
              if (base && norm.indexOf(base) !== 0) return null;
              const rest = norm.slice(base.length).split('/').filter(Boolean);
              return rest.reduce(
                (walk, segment, idx) =>
                  walk.then((dir) => {
                    if (!dir) return null;
                    return listFiles(dir).then((entries) => {
                      const next = entries.filter((e) => nameOf(e) === segment)[0];
                      if (!next) return null;
                      const last = idx === rest.length - 1;
                      if (last) return isDir(next) ? null : next;
                      return isDir(next) ? next : null;
                    });
                  }),
                Promise.resolve(root)
              );
            });
          }),
        Promise.resolve(null)
      );
    });
  });
}

/** Wie tief und wie lange das Inventar laufen darf. */
const INVENTORY_DEPTH = 5;
const INVENTORY_LISTINGS = 60;
const INVENTORY_DEADLINE_MS = 18000;

export default class CloneUpload {
  /**
   * Was auf dem Stick liegt - vollstaendig, mit Pfad und Groesse.
   *
   * Anlass: der `Clone`-Ordner ist ein ganzer Baum (art-app, avoc, settings,
   * vconf, was ...), und einzelne Dateien daraus sind interessant - allen
   * voran `was/clone.xml`, in der die zu installierenden Apps stehen. Die
   * Verteilung selbst laeuft weiter ueber die eine `.sclone`; dieses Inventar
   * ist zum Ansehen und zum gezielten Herunterladen einzelner Dateien.
   *
   * Die Antwort ist ihr eigener Ack und muss deshalb in das Zeitfenster des
   * Kommandokanals passen: Tiefe, Anzahl der Auflistungen und eine harte
   * Frist begrenzen den Lauf. Was nicht mehr angesehen wurde, steht als
   * solches drin - ein abgeschnittenes Inventar ist brauchbar, ein
   * abgelaufenes Kommando nicht.
   */
  static inventory() {
    if (!DeviceType.isTizen && !DeviceType.isOrsay) {
      return Promise.resolve({ ok: false, detail: 'nur auf Samsung' });
    }
    if (!fs()) return Promise.resolve({ ok: false, detail: 'tizen.filesystem nicht vorhanden' });

    const until = Date.now() + INVENTORY_DEADLINE_MS;
    const budget = makeBudget(INVENTORY_LISTINGS);
    const entries = [];
    let truncated = false;

    const walk = (dir, where, depth) => {
      if (Date.now() > until || !budget.take()) {
        truncated = true;
        return Promise.resolve();
      }
      return listFiles(dir).then((list) => {
        const dirs = [];
        for (let i = 0; i < list.length; i += 1) {
          const f = list[i];
          const name = nameOf(f);
          const dirFlag = isDir(f);
          entries.push({
            path: where + '/' + name,
            full: String(f.fullPath || ''),
            name: name,
            dir: dirFlag,
            size: dirFlag ? undefined : sizeOf(f),
          });
          if (dirFlag && !SKIP_DIR_RE.test(name) && depth < INVENTORY_DEPTH) dirs.push(f);
        }
        return dirs.reduce(
          (chain, sub) => chain.then(() => walk(sub, where + '/' + nameOf(sub), depth + 1)),
          Promise.resolve()
        );
      });
    };

    return storageLabels()
      .then((labels) => {
        if (!labels.length) return null;
        return labels.reduce(
          (chain, label) =>
            chain.then(() =>
              resolveDir(label).then((dir) => (dir ? walk(dir, label, 1) : undefined))
            ),
          Promise.resolve()
        );
      })
      .then(() => ({
        ok: true,
        entries: entries,
        truncated: truncated,
        detail: entries.length + ' Eintraege' + (truncated ? ' (abgeschnitten)' : ''),
      }))
      .catch((e) => ({ ok: false, detail: String((e && e.message) || e) }));
  }

  /**
   * Clonefile suchen, lesen und hochladen.
   *
   * `url` kommt aus dem Kommando (SocketAdmin, mit kurzlebigem Token).
   * `path` ist optional - ohne wird auf den Wechselmedien gesucht.
   *
   * Liefert immer ein Ergebnisobjekt, nie eine Ausnahme: der Aufrufer meldet
   * es als Ack zurueck, und "nichts gefunden" ist dort eine so gueltige
   * Antwort wie ein Erfolg.
   */
  static run({ url, path, onProgress }) {
    const step = (p) => {
      console.log('[clone] ' + JSON.stringify(p));
      if (typeof onProgress === 'function') {
        try {
          onProgress(p);
        } catch (e) {
          /* der Melder darf den Upload nicht anhalten */
        }
      }
    };
    if (!url) return Promise.resolve({ ok: false, detail: 'keine Upload-Url im Kommando' });
    if (!DeviceType.isTizen && !DeviceType.isOrsay) {
      return Promise.resolve({
        ok: false,
        detail: 'nur auf Samsung: LG kann Dateien nur auf den Stick schreiben, nicht von ihm lesen',
      });
    }
    if (!fs()) return Promise.resolve({ ok: false, detail: 'tizen.filesystem nicht vorhanden' });

    // Was unterwegs zu sehen war. Geht die Suche leer aus, ist das die
    // Antwort auf die einzige Frage, die dann zaehlt: wo hat sie gesucht?
    const trace = [];

    const find = path
      ? resolveByPath(path)
      : storageLabels().then((labels) => {
          trace.push('Wechselmedien: ' + (labels.length ? labels.join(', ') : 'keine'));
          if (!labels.length) return null;
          const budget = makeBudget(MAX_LISTINGS);
          return labels.reduce(
            (chain, label) =>
              chain.then((found) => found || findCloneFile(label, trace, budget)),
            Promise.resolve(null)
          );
        });

    return find
      .then((file) => {
        if (!file) {
          return {
            ok: false,
            detail:
              (path
                ? 'Datei nicht gefunden: ' + path
                : 'kein Clonefile (*.sclone) gefunden') +
              (trace.length ? ' — gesehen: ' + trace.join(' | ') : ''),
            seen: trace,
          };
        }
        const name = nameOf(file);
        const size = sizeOf(file);
        step({ found: name, size: size, seen: trace });
        // Erst ueber die Adresse, dann nativ. Der native Weg hat schon ein
        // Geraet umgeworfen; er bleibt nur als Rueckfall.
        return readViaUri(file)
          .catch((e) => {
            step({ found: name, uriFailed: String((e && e.message) || e) });
            return readBase64(file);
          })
          .then((base64) => {
            step({ found: name, size: size, read: base64.length, uploading: true });
            return post(url, { fileName: name, size: size, base64: base64 }).then((res) => ({
              ok: true,
              fileName: name,
              size: size,
              stored: res && res.name,
              storedSize: res && res.size,
              seen: trace,
            }));
          });
      })
      .catch((e) => ({
        ok: false,
        detail: String((e && (e.message || e.name)) || e),
      }));
  }
}



// WEBPACK FOOTER //
// ./src/helpers/CloneUpload.js